import React, { Fragment, useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

// Contexts
import OrderContext from "../../../../../../context/orders/v1/OrdersContext";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  rowBottom: {
    marginBottom: "0.5rem",
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: "bold",
    marginTop: "0.3rem",
  },
  emphasize: {
    fontWeight: "bold",
  },
  divider: {
    marginTop: "1rem",
    marginBottom: "1rem",
  },
  alertBox: {
    marginTop: "1rem",
  },
}));

const RefundSummary = () => {
  const classes = useStyles();
  const {
    modalValues,
    modalErrors,
    setValues,
    setErrors,
    order,
    refund_error,
    loading,
    calculateOrderRefund,
  } = useContext(OrderContext);

  const [totalItems, setTotalItems] = useState(0);
  const [subtotal, setSubtotal] = useState("0.00");
  const [total, setTotal] = useState("0.00");
  const [customTotal, setCustomTotal] = useState("0.00");
  const [validShipping, setValidShipping] = useState(true);

  const handleChange = (e) => {
    if (e.target.id === "notify_customer") {
      setValues({ ...modalValues, [e.target.id]: e.target.checked });
    } else if (e.target.id === "total") {
      const id = "custom_total";
      const value = e.target.value;
      const max_refund = parseFloat(modalValues.available_refund);

      // Regex
      const validPricing = /^[0-9]{0,}\.{0,1}[0-9]*$/;

      if (!validPricing.test(value)) {
        modalErrors[id] = "Shipping refund must be a numeric value";
      } else if (parseFloat(value) > max_refund) {
        modalErrors[id] = "Can’t refund more than available";
      } else {
        modalErrors[id] = "";
      }

      setCustomTotal(value);
      setErrors(modalErrors);
    }
  };

  useEffect(() => {
    if (modalValues.items) {
      // Get Items' Price
      let pricing = {};
      order.line_items.map((each) => {
        pricing[each.id] = each.price;
      });

      // Validate Shipping
      let shipping = 0.0;
      if (modalErrors.shipping) {
        setValidShipping(false);
      } else {
        setValidShipping(true);
        shipping = parseFloat(modalValues.shipping).toFixed(2);
      }

      // Update Total Items & Subtotal & Total
      let quantity = 0;
      let subtotal = 0.0;
      let total = 0.0;
      modalValues.items.map((each) => {
        const per_price = pricing[each.line_item_id];
        subtotal += parseFloat(per_price * each.quantity);
        quantity += each.quantity;
      });

      total = subtotal + parseFloat(shipping);

      setTotal(total.toFixed(2));

      if (quantity > 0) {
        setTotalItems(quantity);
        setSubtotal(subtotal.toFixed(2));
      } else {
        setTotalItems(0);
        setSubtotal(subtotal.toFixed(2));
      }
    }
  });

  useEffect(() => {
    // Update State
    setValues({ ...modalValues, subtotal, total });

    // Reset Custom Total
    setCustomTotal("0.00");

    if (!refund_error) {
      calculateOrderRefund(
        order.store_name,
        order.id,
        modalValues.items,
        parseFloat(modalValues.shipping).toFixed(2)
      );
    }
  }, [subtotal, total]);

  useEffect(() => {
    // Update State
    setValues({
      ...modalValues,
      custom_total: parseFloat(customTotal).toFixed(2),
    });
  }, [customTotal]);

  return (
    <Grid container direction="column" justifyContent="flex-start">
      <Paper className={classes.paper}>
        <Grid
          item
          container
          xs={12}
          direction="column"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          {/* Title */}{" "}
          <Grid item className={classes.rowBottom}>
            <Typography variant="body1" className={classes.title}>
              Summary{" "}
            </Typography>{" "}
          </Grid>
          {/* Summary */}{" "}
          <Grid
            item
            container
            xs={12}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            {totalItems === 0 && parseFloat(modalValues.shipping) <= 0 ? (
              <Grid item> No items selected. </Grid>
            ) : loading ? (
              <Grid item> Updating... </Grid>
            ) : (
              <Fragment>
                <Grid
                  item
                  container
                  xs={7}
                  spacing={1}
                  direction="column"
                  style={{
                    textAlign: "left",
                  }}
                >
                  <Grid item>
                    Items subtotal <br /> ({totalItems}
                    items){" "}
                  </Grid>{" "}
                  <Grid item className={classes.rowBottom}>
                    Shipping{" "}
                  </Grid>{" "}
                  <Grid item className={classes.emphasize}>
                    Refund total{" "}
                  </Grid>{" "}
                </Grid>
                <Grid
                  item
                  container
                  xs={5}
                  spacing={1}
                  direction="column"
                  style={{
                    textAlign: "right",
                  }}
                >
                  <Grid item>
                    $ {subtotal} <br />& nbsp;{" "}
                  </Grid>{" "}
                  <Grid item className={classes.rowBottom}>
                    ${" "}
                    {validShipping
                      ? parseFloat(modalValues.shipping).toFixed(2)
                      : "0.00"}{" "}
                  </Grid>{" "}
                  <Grid item className={classes.emphasize}>
                    $ {total}{" "}
                  </Grid>{" "}
                </Grid>{" "}
              </Fragment>
            )}{" "}
          </Grid>
          {/* Refund Amount */}{" "}
          <Grid item container xs={12}>
            <Grid item xs={12}>
              <Divider className={classes.divider} orientation="horizontal" />
            </Grid>
            <Grid item className={classes.rowBottom}>
              <span
                style={{
                  fontSize: "0.8rem",
                  fontWeight: "bold",
                }}
              >
                REFUND AMOUNT{" "}
              </span>{" "}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="text"
                variant="outlined"
                size="small"
                margin="dense"
                id="total"
                InputProps={{
                  // readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <span
                        style={{
                          fontSize: "0.9rem",
                        }}
                      >
                        {" "}
                        ${" "}
                      </span>{" "}
                    </InputAdornment>
                  ),
                }}
                value={
                  !loading
                    ? parseFloat(customTotal) !== 0
                      ? customTotal
                      : parseFloat(total).toFixed(2)
                    : "..."
                }
                onChange={handleChange}
                error={modalErrors.custom_total ? true : false}
                helperText={
                  modalErrors.custom_total ? modalErrors.custom_total : ""
                }
              />{" "}
            </Grid>
            <Grid item>
              <Typography variant="body2">
                $ {modalValues.available_refund}
                available for refund{" "}
              </Typography>{" "}
            </Grid>
            {parseFloat(customTotal) !== 0 && (
              <Grid item className={classes.alertBox}>
                <Alert
                  severity="info"
                  style={{
                    textAlign: "left",
                  }}
                >
                  The amount you’ re refunding is different from the suggested
                  refund total.{" "}
                </Alert>{" "}
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider className={classes.divider} orientation="horizontal" />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    id="notify_customer"
                    checked={
                      modalValues.notify_customer
                        ? modalValues.notify_customer
                        : false
                    }
                    onChange={handleChange}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    style={{
                      textAlign: "left",
                    }}
                  >
                    Send a notification to the customer{" "}
                  </Typography>
                }
              />{" "}
            </Grid>{" "}
          </Grid>{" "}
        </Grid>{" "}
      </Paper>{" "}
    </Grid>
  );
};

export default RefundSummary;
